/**
 * Team Leader Quick Start Guide
 *
 * Daily workflow guide for team leaders showing what to do and when.
 * Part of the DOC-06 role-specific quick start guides.
 */

export const tlQuickStartContent = {
  id: 'quickstart-tl',
  title: 'Team Leader Quick Start Guide',
  category: 'quickstart',
  roles: ['team_leader'],
  keywords: [
    'getting started', 'team leader', 'TL', 'pulse', 'commitments',
    'first steps', 'how to start', 'onboarding', 'tutorial', 'daily workflow', 'data entry'
  ],

  sections: [
    {
      title: 'Welcome, Team Leader!',
      content: `As a Team Leader, you enter daily pulse data for your gym and track your commitments.

**Your main responsibilities:**
- Submit accurate pulse entries on time (4 times per day)
- Verify your entries after submission
- Fulfill your closure commitments`
    },
    {
      title: 'Your Daily Pulse Entries',
      content: `You need to submit pulse data 4 times each day, at the end of each pulse window.

| Pulse | Time Window | Submit By | What It Covers |
|-------|-------------|-----------|----------------|
| **P1** | 8:00 AM - 12:00 PM | 12:00 PM | Morning shift activities |
| **P2** | 12:00 PM - 4:00 PM | 4:00 PM | Early afternoon activities |
| **P3** | 4:00 PM - 8:00 PM | 8:00 PM | Late afternoon activities |
| **P4** | 8:00 PM - 10:00 PM | 10:00 PM | Evening activities |

**How to submit:**
1. **Go to:** Pulse Entry
2. **Select:** Your gym (if not auto-selected)
3. **Enter:** Your numbers for the current pulse period
4. **Click:** Submit

*Important: Submit before the window closes! Late submissions are flagged.*`
    },
    {
      title: 'Understanding the Fields',
      content: `Here's what each field means on the Pulse Entry form:

| Field | What to Enter | Example |
|-------|---------------|---------|
| **Dialed** | Total calls made during this pulse | 50 |
| **Connected** | Calls that were answered | 35 |
| **Visited** | People who came to the gym | 12 |
| **Closures** | Memberships sold | 3 |
| **FD Available** | Front desk staff present | 2 |

**When entering closures:**
- Click "Add Closure" for each sale
- Enter customer name, package type, and revenue amount
- This creates a commitment that needs to be fulfilled

**Tips:**
- Enter data accurately - don't inflate numbers
- If you made a mistake, you can edit before verification
- FD Available = 0 is valid if the front desk is closed`
    },
    {
      title: 'After Submitting',
      content: `After you submit, you need to verify your entry.

**Step 1: TL Verify**
- After submitting, click **TL Verify**
- This marks your entry as reviewed and ready for manager approval
- You can still edit until you verify

**Step 2: Wait for Manager Verification**
- Your manager will review and verify your entry
- You'll see the status change to "Manager Verified"

**Track Your Commitments:**
- **Go to:** Commitment Dashboard
- **See:** Your gym's closure commitments
- **Action:** Mark commitments as "Fulfilled" when the customer completes payment`
    },
    {
      title: 'Fulfilling Commitments',
      content: `When you enter closures, you create commitments. Here's how to fulfill them:

**What is a commitment?**
A commitment is a sale (closure) you've entered that needs to be completed. The customer signed up, but the sale isn't final until payment is processed.

**To fulfill a commitment:**
1. **Go to:** Commitment Dashboard
2. **Find:** Your pending closure
3. **Click:** "Fulfill" when the customer has paid
4. **Enter:** Final confirmation details

**Commitment deadlines:**
- Commitments should be fulfilled within 48 hours
- Overdue commitments appear in red
- Your manager will follow up on overdue items`
    },
    {
      title: 'Quick Links',
      links: [
        { label: 'Pulse Entry', path: '/pulse-entry', description: 'Submit your pulse data' },
        { label: 'Commitment Dashboard', path: '/commitment-dashboard', description: 'Track and fulfill closures' },
        { label: 'Command Center', path: '/', description: 'See overall performance' },
        { label: 'Verification', path: '/verification', description: 'Check verification status' }
      ]
    },
    {
      title: 'Pro Tips',
      content: `**Be successful as a Team Leader:**

1. **Set alarms** - Add phone alarms 10 minutes before each pulse window ends (11:50, 3:50, 7:50, 9:50)

2. **Submit early** - Don't wait until the last minute. Network issues or app problems could make you late.

3. **Enter closures immediately** - When you make a sale, enter it right away so you don't forget the details

4. **TL Verify right after submit** - Make it a habit to verify immediately after submitting

5. **Check your commitments daily** - Don't let unfulfilled commitments pile up

**Common TL questions:**
- "I missed the window - can I still submit?" - Yes, but it will be flagged as late. Submit as soon as you can.
- "I made a mistake after verifying - what do I do?" - Contact your manager to un-verify so you can edit.
- "What if I have no closures?" - Enter 0 for closures. That's valid data.
- "What if FD is closed?" - Enter 0 for FD Available. Add a note if needed.`
    }
  ]
};

export default tlQuickStartContent;
